import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Settings2, LoaderCircle, ArrowUp, CornerLeftUp, RotateCw, Upload, Folder,
  File as FileIcon,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import { formatBytes } from '../../utils/formatBytes';
import { usePreferencesStore } from '../../store/preferencesStore';
import { useFtpStore } from '../../store/ftpStore';
import TruncatedText from '../ui/TruncatedText';
import Tooltip from '../ui/Tooltip';
import { rowDropKind, INTERNAL_DND_MIME, dndSourceMime } from '../../utils/ftpRowDrop';
import { fmtMtime, parentLocal, parentRemote } from './ftpViewHelpers';
import { CtxMenu } from './FtpCtxMenu';

/* ── One file-browser pane ──────────────────────────────────────────────── */

/**
 * Per-pane settings popover. Today it holds one checkbox; it exists as a
 * popover rather than a bare toggle button because "show hidden files" is the
 * first of a family (follow symlinks, preserve timestamps) that all belong in
 * the same place.
 *
 * It renders in BOTH panes and writes ONE preference — the toggle is global, so
 * local and remote can never disagree about what "hidden" means. Toggling
 * refreshes both panes: the pref is read at list time, so without a refresh the
 * change would appear only on the next navigation.
 */
export function FtpPaneSettings({ t }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const showHidden = usePreferencesStore((s) => s.ftpShowHiddenFiles);
  const updatePreference = usePreferencesStore((s) => s.updatePreference);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggleHidden = (next) => {
    updatePreference('ftpShowHiddenFiles', next);
    // Re-list both panes so the change is visible now rather than on the next
    // navigation. Failures are swallowed: the panes surface their own errors,
    // and a refresh that fails must not make the checkbox look stuck.
    const store = useFtpStore.getState();
    store.listLocal(store.localPath || null).catch(() => {});
    const remotePath = store.getRemotePath();
    if (remotePath) store.listRemote(remotePath).catch(() => {});
  };

  return (
    <div className="ftp-pane-settings" ref={wrapRef}>
      <Tooltip content={t('ftp.paneSettings')} side="bottom" disabled={open}>
        <button
          type="button"
          className={`ftp-tool-btn${open ? ' ftp-tool-btn--active' : ''}`}
          aria-label={t('ftp.paneSettings')}
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <Settings2 size={13} />
        </button>
      </Tooltip>
      {open && (
        <div className="ftp-pane-settings-pop" role="dialog" aria-label={t('ftp.paneSettings')}>
          <label className="ftp-pane-settings-row">
            <input
              type="checkbox"
              className="checkbox-brand cursor-pointer"
              checked={!!showHidden}
              onChange={(e) => toggleHidden(e.target.checked)}
            />
            <span>
              <span className="ftp-pane-settings-label">{t('ftp.showHiddenFiles')}</span>
              <span className="ftp-pane-settings-desc">{t('ftp.showHiddenFilesDesc')}</span>
            </span>
          </label>
        </div>
      )}
    </div>
  );
}

export function FtpPane({
  side, title, path, entries, loading, error, selection, setSelection, navigate, refresh,
  toolbarExtras, onEntryOpen, buildMenuItems,
  // Optional: right-clicking blank space does nothing without it, which is
  // exactly the behaviour this prop was added to fix.
  buildBackgroundMenuItems,
  // Optional: drop a dragged selection onto a folder ROW. Without it, folder
  // rows are not drop targets and only the pane is, which is why there was no
  // move in either direction.
  onDropRow,
  // Optional: OS files (or a drag from the other pane) dropped ONTO a folder row
  // go INTO that folder. Remote pane only.
  onDropInto,
  // Optional keyboard actions. Absent on the local pane, which has neither a
  // delete nor a rename behind it; the keys then do nothing rather than
  // appearing to work.
  onDeleteSelection, onRenameEntry, onDropPane, confirmBar, t,
}) {
  const [pathDraft, setPathDraft] = useState(path || '');
  const [menu, setMenu] = useState(null); // { x, y, entry } — entry null = background
  // The row a Shift-range grows FROM: the last row committed with a plain or
  // Ctrl click. Deliberately not "the last selected path", which is ambiguous
  // once a range exists and makes repeated Shift-clicks accumulate.
  const anchorRef = useRef(null);
  // The folder row currently under the pointer during a same-pane drag.
  const [dropDir, setDropDir] = useState(null);
  const paneRef = useRef(null);

  /**
   * Click anywhere else in the window → this pane's selection clears, the way
   * a file manager behaves.
   *
   * Not cleared by a click that is ACTING on the selection, or the rows would
   * visibly deselect under the very action being chosen:
   *  - a row in THIS pane (its own click handler decides — Ctrl/Shift extend);
   *  - the context menu, a modal and its backdrop;
   *  - a scrollbar, since scrolling to reach a row is not "clicking away".
   * Every action that reads the selection captures it when the menu or modal
   * OPENS, so none of them depend on it surviving; these exclusions are about
   * what the user sees, not about correctness.
   *
   * `mousedown` in the capture phase, so it runs before anything that might
   * stop propagation, and only while there is something to clear.
   */
  useEffect(() => {
    if (selection.length === 0) return undefined;
    const onDown = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const pane = paneRef.current;
      // A RIGHT-click inside this pane opens a menu, and `mousedown` fires
      // before `contextmenu` — so without this, right-clicking blank space
      // cleared the selection a frame before the background menu (whose contract
      // is to leave it alone) appeared. Found in review, 13.09.
      if (e.button === 2 && pane && pane.contains(target)) return;
      if (pane && pane.contains(target) && target.closest('tr.ftp-row:not(.ftp-row--up)')) return;
      if (target.closest('.ftp-ctx-menu, .modal-content, .modal-backdrop, .os-scrollbar')) return;
      setSelection([]);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [selection.length, setSelection]);
  // Column sort. Per pane, and deliberately NOT persisted: it is a way to look
  // at a directory, not a preference about the app.
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [dragOver, setDragOver] = useState(false);
  const [openingPath, setOpeningPath] = useState(null); // dir row awaiting its listing
  const dragDepthRef = useRef(0);
  // OS-style type-ahead: typed prefix + last keystroke time (1s reset window).
  const typeAheadRef = useRef({ text: '', time: 0 });
  const rowRefs = useRef(new Map()); // entry.path -> <tr> for scroll-into-view

  useEffect(() => {
    setPathDraft(path || '');
  }, [path]);

  // Clear the per-row "opening" spinner once navigation settles (loading went
  // false) or the pane moved to another directory (success or failure).
  useEffect(() => {
    if (!loading) setOpeningPath(null);
  }, [loading, path]);

  // Double-click / Enter-to-open: acknowledge the gesture instantly by spinning
  // the folder's own icon, then kick off the listing. Files fall through to the
  // parent's open handler (download/upload), which surface in the transfers strip.
  const handleEntryOpen = (entry) => {
    if (entry.isDir) setOpeningPath(entry.path);
    onEntryOpen(entry);
  };

  /**
   * The rows as displayed. **Directories always come first**, whatever the
   * column and whatever the direction — that is what a file pane means, and
   * sorting them in among the files by size (where a directory has none) would
   * scatter the one thing people navigate by.
   *
   * `entries` is the prop and stays untouched; every consumer below reads
   * `rows`, so the keyboard, the ranges and the drag payloads all agree with
   * what is on screen. A Shift-range that used the unsorted order while the
   * user looked at a sorted list would select rows they never saw.
   */
  const rows = useMemo(() => {
    const factor = sort.dir === 'asc' ? 1 : -1;
    const byName = (a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, {
        sensitivity: 'base',
        numeric: true, // "file10" after "file9", which is what people mean
      });
    const compare = (a, b) => {
      if (!!a.isDir !== !!b.isDir) return a.isDir ? -1 : 1;
      let out = 0;
      if (sort.key === 'size') out = (Number(a.size) || 0) - (Number(b.size) || 0);
      else if (sort.key === 'mtime') out = (Number(a.mtime) || 0) - (Number(b.mtime) || 0);
      else out = byName(a, b);
      // Ties fall back to the name, so a directory of same-sized files does not
      // reshuffle itself on every re-render.
      return (out || byName(a, b)) * (out === 0 ? 1 : factor);
    };
    return [...entries].sort(compare);
  }, [entries, sort]);

  const toggleSort = (key) =>
    setSort((cur) => ({ key, dir: cur.key === key && cur.dir === 'asc' ? 'desc' : 'asc' }));

  const canUp =
    side === 'remote' ? parentRemote(path) !== path : parentLocal(path) !== path;

  const parentPath = side === 'remote' ? parentRemote(path) : parentLocal(path);
  // The `..` row as an entry, so it goes through the SAME drop guards as any
  // folder row rather than a second copy of them.
  const upEntry = { path: parentPath, name: '..', isDir: true };

  const goUp = () => {
    if (!canUp) return;
    navigate(side === 'remote' ? parentRemote(path) : parentLocal(path));
  };

  /**
   * Does the PANE (not a folder row) accept this drag — and so show its
   * "drop files to upload" overlay?
   *
   * **Not for a drag that started in this same pane.** Those files are already
   * here; dropping them on the pane does nothing, and an overlay promising an
   * upload is simply wrong. A same-pane drag is a MOVE, and the only valid
   * targets for it are folder rows and `..`, which light up on their own.
   * Read from the drag TYPE, since `getData` is unavailable during dragover.
   */
  const acceptsDrop = (e) => {
    const types = Array.from(e.dataTransfer?.types || []);
    const internal = types.includes(INTERNAL_DND_MIME);
    const fromHere = types.includes(dndSourceMime(side));
    const hasFiles = types.includes('Files');
    return (internal && !fromHere) || (side === 'remote' && hasFiles);
  };

  // Depth counts EVERY drag, so leaving the pane always clears both the
  // pane overlay and a folder row's highlight; only drags the pane accepts
  // turn the overlay on.
  const clearDrag = () => {
    dragDepthRef.current = 0;
    setDragOver(false);
    setDropDir(null);
  };
  const handleDragEnter = (e) => {
    dragDepthRef.current += 1;
    if (!acceptsDrop(e)) return;
    e.preventDefault();
    setDragOver(true);
  };
  /**
   * The drop TARGET follows the pointer: over a folder row that accepts this
   * drag, that row (and only it) is the target; anywhere else, the pane.
   * Decided here, on the pane, from `e.target` — not from per-row
   * dragenter/dragleave, which fire on every CELL crossing and made the row
   * highlight blink. Same path → same state → no re-render.
   */
  const handleDragOver = (e) => {
    const target = rowDropTarget(e);
    if (target) {
      e.preventDefault();
      e.dataTransfer.dropEffect = target.kind === 'move' ? 'move' : 'copy';
      setDropDir(target.entry.path);
      return;
    }
    setDropDir(null);
    if (!acceptsDrop(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const handleDragLeave = () => {
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) clearDrag();
  };
  const handleDrop = (e) => {
    const target = rowDropTarget(e);
    e.preventDefault();
    clearDrag();
    if (target?.kind === 'move') onDropRow(target.entry, e);
    else if (target?.kind === 'into') onDropInto(target.entry, e);
    else if (acceptsDrop(e)) onDropPane(e);
  };

  // A drag that ends somewhere else (dropped on another pane, cancelled with
  // Escape outside this pane) must not leave a highlight behind.
  useEffect(() => {
    if (!dragOver && !dropDir) return undefined;
    const reset = () => {
      dragDepthRef.current = 0;
      setDragOver(false);
      setDropDir(null);
    };
    window.addEventListener('drop', reset);
    window.addEventListener('dragend', reset);
    return () => {
      window.removeEventListener('drop', reset);
      window.removeEventListener('dragend', reset);
    };
  }, [dragOver, dropDir]);

  /**
   * Click, Ctrl/Cmd-click and **Shift-click**, the way a file manager does it.
   *
   * Shift needs an ANCHOR, and the anchor is not "the last selected row" — with
   * a multi-row selection that is ambiguous. It is the row the user last
   * committed to with a plain or Ctrl click, which is what makes repeated
   * Shift-clicks grow and shrink one range instead of accumulating several.
   */
  const handleRowClick = (entry, e) => {
    const idx = rows.findIndex((en) => en.path === entry.path);
    if (e.shiftKey && anchorRef.current != null) {
      const from = Math.min(anchorRef.current, idx);
      const to = Math.max(anchorRef.current, idx);
      // Replaces rather than unions: Shift-click means "this range", and a
      // union would make the range impossible to shrink again.
      setSelection(rows.slice(from, to + 1).map((en) => en.path));
      return;
    }
    anchorRef.current = idx;
    if (e.metaKey || e.ctrlKey) {
      setSelection(
        selection.includes(entry.path)
          ? selection.filter((p) => p !== entry.path)
          : [...selection, entry.path]
      );
    } else {
      setSelection([entry.path]);
    }
  };

  const handleDragStart = (e, entry) => {
    const paths = selection.includes(entry.path) ? selection : [entry.path];
    const ents = rows
      .filter((en) => paths.includes(en.path))
      .map((en) => ({ path: en.path, name: en.name, isDir: !!en.isDir }));
    e.dataTransfer.setData(INTERNAL_DND_MIME, JSON.stringify({ source: side, entries: ents }));
    e.dataTransfer.setData(dndSourceMime(side), '');
    // 'copyMove', not 'copy': dropping on the other pane copies, dropping on a
    // folder in this one moves, and the cursor should be able to say so.
    e.dataTransfer.effectAllowed = 'copyMove';
  };

  const openMenuAt = (x, y, entry) => {
    if (!selection.includes(entry.path)) setSelection([entry.path]);
    setMenu({ x, y, entry });
  };

  /**
   * Right-click on blank space in the pane. `entry: null` is what tells the
   * renderer to build the background menu instead of a row menu.
   *
   * **The selection is left alone.** Clearing it here would be conventional in
   * a file manager, but this menu carries actions that read the selection, and
   * silently emptying it a frame before the user picks one is the kind of thing
   * that makes a menu feel haunted.
   */
  const openBackgroundMenuAt = (x, y) => setMenu({ x, y, entry: null });

  /* ── Drop ONTO a folder row: move (same pane) or upload into (remote) ──── */

  /** The folder row under the pointer, if this drag may land on it. */
  const rowDropTarget = (e) => {
    const rowEl = e.target?.closest?.('[data-ftp-row]');
    if (!rowEl) return null;
    const path = rowEl.getAttribute('data-ftp-row');
    const entry = path === upEntry.path && rowEl.hasAttribute('data-ftp-up') ? upEntry : rows.find((en) => en.path === path);
    const kind = rowDropKind({
      side,
      entry,
      types: e.dataTransfer?.types,
      selection,
      canMove: !!onDropRow,
      canDropInto: !!onDropInto,
    });
    return kind ? { kind, entry } : null;
  };

  /** Move the cursor to `idx`. `extend` grows the range from the anchor
   *  instead of replacing the selection, which is Shift+Arrow. */
  const selectIndex = (idx, extend = false) => {
    const entry = rows[idx];
    if (!entry) return;
    if (extend && anchorRef.current != null) {
      const from = Math.min(anchorRef.current, idx);
      const to = Math.max(anchorRef.current, idx);
      setSelection(rows.slice(from, to + 1).map((en) => en.path));
    } else {
      anchorRef.current = idx;
      setSelection([entry.path]);
    }
    rowRefs.current.get(entry.path)?.scrollIntoView({ block: 'nearest' });
  };

  // Keyboard on the listing (the body div is focusable): OS file-manager
  // behavior — letters type-ahead to the next matching name (1s buffer;
  // pressing the same letter again cycles through its matches), arrows move
  // the selection, Enter opens it.
  const handlePaneKeyDown = (e) => {
    if (rows.length === 0) return;
    // Ctrl/Cmd+A before the modifier bail below, which exists to keep
    // type-ahead from swallowing application shortcuts.
    if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault();
      setSelection(rows.map((en) => en.path));
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    // With a range selected the cursor is at its FAR end, not its first row:
    // Shift+Down after selecting 3-5 must go to 6, and `findIndex` would send
    // it back to 4.
    const firstIdx = rows.findIndex((en) => selection.includes(en.path));
    const lastIdx = rows.reduce((acc, en, i) => (selection.includes(en.path) ? i : acc), -1);

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      const cursor = delta > 0 ? lastIdx : firstIdx;
      const next =
        cursor < 0
          ? delta > 0
            ? 0
            : rows.length - 1
          : Math.min(rows.length - 1, Math.max(0, cursor + delta));
      selectIndex(next, e.shiftKey);
      return;
    }
    if (e.key === 'Enter') {
      if (firstIdx >= 0) {
        e.preventDefault();
        handleEntryOpen(rows[firstIdx]);
      }
      return;
    }
    // Delete and F2, the two chords every file manager has. Both go through the
    // SAME confirm bar and rename modal the menu uses — a keyboard path that
    // deletes without the confirm would be a second, weaker gate on the one
    // action here that cannot be undone.
    if (e.key === 'Delete' && onDeleteSelection) {
      const chosen = rows.filter((en) => selection.includes(en.path));
      if (chosen.length > 0) {
        e.preventDefault();
        onDeleteSelection(chosen);
      }
      return;
    }
    if (e.key === 'F2' && onRenameEntry) {
      // Rename takes ONE new name, so it acts on the first selected row rather
      // than the whole selection — the same rule the menu already follows.
      if (firstIdx >= 0) {
        e.preventDefault();
        onRenameEntry(rows[firstIdx]);
      }
      return;
    }
    if (e.key.length !== 1 || e.key === ' ') return;

    e.preventDefault();
    const now = Date.now();
    const buf = typeAheadRef.current;
    const stale = now - buf.time > 1000;
    const ch = e.key.toLowerCase();
    const text = (stale ? '' : buf.text) + ch;
    typeAheadRef.current = { text, time: now };

    const names = rows.map((en) => String(en.name || '').toLowerCase());
    // Same letter repeated cycles through that letter's matches; a growing
    // prefix keeps the current row eligible; a fresh single letter starts
    // looking after the current row. All searches wrap.
    const sameChar = text.length > 1 && [...text].every((c) => c === text[0]);
    const needle = sameChar ? text[0] : text;
    const start =
      sameChar || text.length === 1 ? (firstIdx < 0 ? 0 : firstIdx + 1) : Math.max(0, firstIdx);
    for (let i = 0; i < rows.length; i++) {
      const idx = (start + i) % rows.length;
      if (names[idx].startsWith(needle)) {
        selectIndex(idx);
        return;
      }
    }
  };

  return (
    <div ref={paneRef} className={`ftp-pane ftp-pane--${side}`}>
      <div className="ftp-pane-toolbar">
        <span className="ftp-pane-title">{title}</span>
        <input
          type="text"
          className="ftp-path-input"
          value={pathDraft}
          spellCheck={false}
          onChange={(e) => setPathDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && pathDraft.trim()) navigate(pathDraft.trim());
            if (e.key === 'Escape') setPathDraft(path || '');
          }}
        />
        {/* The app's own Tooltip, not a native `title`: a native title is slow
            to appear, unstyled and invisible on touch. */}
        <Tooltip content={t('ftp.up')} side="bottom" disabled={!canUp}>
          <button
            type="button"
            className="ftp-tool-btn"
            aria-label={t('ftp.up')}
            disabled={!canUp}
            onClick={goUp}
          >
            <ArrowUp size={13} />
          </button>
        </Tooltip>
        <Tooltip content={t('ftp.refresh')} side="bottom">
          <button type="button" className="ftp-tool-btn" aria-label={t('ftp.refresh')} onClick={refresh}>
            <RotateCw size={13} />
          </button>
        </Tooltip>
        {(toolbarExtras || []).map((extra) => (
          <Tooltip key={extra.id} content={extra.title} side="bottom">
            <button
              type="button"
              className={`ftp-tool-btn${extra.danger ? ' ftp-tool-btn--danger' : ''}`}
              aria-label={extra.title}
              onClick={extra.onClick}
            >
              <extra.Icon size={13} />
            </button>
          </Tooltip>
        ))}
        <FtpPaneSettings t={t} />
      </div>

      {confirmBar}
      {error && <div className="ftp-pane-error">{error}</div>}

      <div
        className="ftp-pane-body"
        tabIndex={0}
        onKeyDown={handlePaneKeyDown}
        onContextMenu={(e) => {
          // Rows call `preventDefault` and stop here themselves, so reaching
          // this handler means the pointer really was over blank space.
          if (!buildBackgroundMenuItems) return;
          e.preventDefault();
          openBackgroundMenuAt(e.clientX, e.clientY);
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {loading && entries.length > 0 && (
          <>
            <div className="ftp-pane-progress" aria-hidden="true" />
            <div className="ftp-pane-busy" role="status" aria-label={t('ftp.loading')}>
              <LoaderCircle size={13} className="animate-spin" />
              <span>{t('ftp.loading')}</span>
            </div>
          </>
        )}
        <OsScroll style={{ flex: 1, minHeight: 0 }}>
          {loading && entries.length === 0 ? (
            <div className="ftp-pane-loading">
              <LoaderCircle size={18} className="animate-spin" />
            </div>
          ) : entries.length === 0 && !canUp ? (
            <div className="ftp-pane-empty">{t('ftp.emptyDir')}</div>
          ) : (
            <table className="ftp-table">
              <thead>
                <tr>
                  {[
                    { key: 'name', cls: 'ftp-th--name', label: t('ftp.colName') },
                    { key: 'size', cls: 'ftp-th--size', label: t('ftp.colSize') },
                    { key: 'mtime', cls: 'ftp-th--mtime', label: t('ftp.colModified') },
                  ].map((col) => (
                    <th
                      key={col.key}
                      className={`ftp-th ${col.cls}${sort.key === col.key ? ' ftp-th--sorted' : ''}`}
                      aria-sort={
                        sort.key === col.key
                          ? sort.dir === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <button
                        type="button"
                        className="ftp-th-btn"
                        onClick={() => toggleSort(col.key)}
                      >
                        <span>{col.label}</span>
                        {/* The arrow is rendered only for the active column, so
                            "sorted by this" is readable at a glance rather than
                            three indicators competing. */}
                        {sort.key === col.key &&
                          (sort.dir === 'asc' ? (
                            <ChevronUp size={11} aria-hidden />
                          ) : (
                            <ChevronDown size={11} aria-hidden />
                          ))}
                      </button>
                    </th>
                  ))}
                </tr>
                {/* `..` — ALWAYS present when there is a parent, including in an
                    empty folder, and pinned WITH the header (it lives in <thead>, which sticks as one unit) so it
                    is still a drop target when you drag a file from far down a long list.
                    It is not part of `rows`, so it can never be selected,
                    ranged, Select-all'd, dragged or deleted. */}
                {canUp && (
                  <tr
                    className={`ftp-row ftp-row--up${
                      dropDir === parentPath ? ' ftp-row--drop-target' : ''
                    }`}
                    onDoubleClick={goUp}
                    onContextMenu={(e) => {
                      // No menu: every action there would target a folder the
                      // user is not in. And do not fall through to the pane's
                      // background menu either.
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    data-ftp-row={upEntry.path}
                    data-ftp-up=""
                  >
                    <td className="ftp-td ftp-td--name">
                      <span className="ftp-row-icon ftp-row-icon--dir">
                        <CornerLeftUp size={13} />
                      </span>
                      <span className="ftp-row-name">..</span>
                    </td>
                    <td className="ftp-td ftp-td--size" />
                    <td className="ftp-td ftp-td--mtime" />
                  </tr>
                )}
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td className="ftp-td ftp-pane-empty" colSpan={3}>
                      {t('ftp.emptyDir')}
                    </td>
                  </tr>
                )}
                {rows.map((entry) => (
                  <tr
                    key={entry.path}
                    ref={(el) => {
                      if (el) rowRefs.current.set(entry.path, el);
                      else rowRefs.current.delete(entry.path);
                    }}
                    className={`ftp-row group${
                      selection.includes(entry.path) ? ' ftp-row--selected' : ''
                    }${dropDir === entry.path ? ' ftp-row--drop-target' : ''}`}
                    draggable
                    onClick={(e) => handleRowClick(entry, e)}
                    onDoubleClick={() => handleEntryOpen(entry)}
                    onDragStart={(e) => handleDragStart(e, entry)}
                    data-ftp-row={entry.path}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      // Without this the event reaches the pane body's handler,
                      // which replaces this row menu with the background one.
                      e.stopPropagation();
                      openMenuAt(e.clientX, e.clientY, entry);
                    }}
                  >
                    <td className="ftp-td ftp-td--name">
                      <span className={`ftp-row-icon${entry.isDir ? ' ftp-row-icon--dir' : ''}`}>
                        {openingPath === entry.path ? (
                          <LoaderCircle size={13} className="animate-spin" />
                        ) : entry.isDir ? (
                          <Folder size={13} />
                        ) : (
                          <FileIcon size={13} />
                        )}
                      </span>
                      <TruncatedText className="ftp-row-name" text={entry.name} />
                      <Tooltip content={t('ftp.moreActions')} side="left">
                      <button
                        type="button"
                        className="ftp-row-menu-btn opacity-0 group-hover:opacity-100"
                        aria-label={t('ftp.moreActions')}
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          openMenuAt(rect.left, rect.bottom + 4, entry);
                        }}
                      >
                        <MoreHorizontal size={12} />
                      </button>
                      </Tooltip>
                    </td>
                    <td className="ftp-td ftp-td--size">
                      {entry.isDir ? '—' : formatBytes(entry.size)}
                    </td>
                    <td className="ftp-td ftp-td--mtime">{fmtMtime(entry.mtime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </OsScroll>

        {/* The whole-list overlay only when the target IS the list: over a
            folder row, that row alone is highlighted. */}
        {dragOver && !dropDir && (
          <div className="ftp-drop-overlay">
            <Upload size={18} />
            <span>{side === 'remote' ? t('ftp.dropToUpload') : t('ftp.download')}</span>
          </div>
        )}
      </div>

      {menu && (
        <CtxMenu
          pos={{ x: menu.x, y: menu.y }}
          items={menu.entry ? buildMenuItems(menu.entry) : buildBackgroundMenuItems()}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}
